import type { AlignmentDocument } from './alignment-document';
import type { AISession } from '@/types/ai-session';

export const AI_SESSION_TYPE = {
  alignment: 1,
  asset: 3,
  storyboard: 4,
} as const;

export type CanonicalUnknownObject = Readonly<Record<string, unknown>>;

export const CANONICAL_STAGE_STATUSES = [
  'created',
  'running',
  'waiting_user',
  'checklist_frozen',
  'draft_ready',
  'confirmed',
  'completed',
  'dirty',
  'suspended',
  'failed',
  'cancelled',
] as const;

export type CanonicalStageStatus = (typeof CANONICAL_STAGE_STATUSES)[number];

export function isCanonicalStageStatus(value: unknown): value is CanonicalStageStatus {
  return (
    typeof value === 'string' && (CANONICAL_STAGE_STATUSES as readonly string[]).includes(value)
  );
}

export type CanonicalBatchError = Readonly<{
  batchIndex?: number;
  message?: string;
}>;

export type CanonicalPayloadBase = {
  error?: string;
  batchErrors?: readonly CanonicalBatchError[];
  degraded?: boolean;
  modelName?: string | null;
  stageModelNames?: Readonly<Record<string, string>>;
  /** 资产 / 分镜运行中的进度快照，带 runId，只用于恢复同一 Run 的进度卡。 */
  lastAgentProgress?: CanonicalUnknownObject;
};

export type CanonicalAlignmentAnswer = Readonly<{
  questionId: string;
  answerType: string;
  selectedOption?: string;
  content?: string;
  title?: string;
  extensions?: CanonicalUnknownObject;
}>;

export type CanonicalAlignmentVersion = Readonly<{
  accepted?: boolean;
  savedAt?: string;
  artifactId?: string;
  artifactVersion?: number;
  sessionVersion?: number;
  /** ADR-client-001 W1：server 在 version 条目顶层写入，契约已声明，禁止再经 extensions 双读 */
  editChange?: CanonicalUnknownObject;
  extensions?: CanonicalUnknownObject;
}>;

export type CanonicalAlignmentEnvelope = Readonly<{
  document?: AlignmentDocument;
  answers: readonly CanonicalAlignmentAnswer[];
  alignmentPackage: CanonicalUnknownObject;
  alignmentPackageGeneratedAt?: string;
  alignmentPackageError?: string;
  alignmentPackageFailedAt?: string;
  compiledSuggestions: string;
  lastAgentProgress?: CanonicalUnknownObject;
  versions: readonly CanonicalAlignmentVersion[];
  skillSnapshot: CanonicalUnknownObject;
  skillSource: 'system' | 'custom';
  skillSelectionSource?: string;
  referencedSessionIds: readonly string[];
}>;

export type CanonicalBreakdownEnvelope = Readonly<{
  breakdownStrategy: CanonicalUnknownObject;
  confirmed: boolean;
  submittedAt?: string;
  workflowArtifactDirty: boolean;
  initialBreakdownArtifactVersion?: number;
  readingStructured: CanonicalUnknownObject;
}>;

export type CanonicalAlignmentCheckpoint = Readonly<{
  type?: string;
  runKind?: string;
  stage?: string;
  status?: string;
  terminalReason?: string;
  waitingAction?: string;
  suspendReason?: string;
  recommendation?: CanonicalAlignmentCheckpointRecommendation;
  agentProgress?: CanonicalUnknownObject;
  lastAgentProgress?: CanonicalUnknownObject;
  extensions?: CanonicalUnknownObject;
}>;

export type CanonicalAlignmentCandidateType = Readonly<{
  type: string;
  label?: string;
  score?: number;
}>;

export type CanonicalAlignmentCheckpointRecommendation = Readonly<{
  detectedScriptType?: string;
  detectedScriptTypeLabel?: string;
  typeConfidence?: number;
  candidateTypes: readonly CanonicalAlignmentCandidateType[];
  evidence: readonly string[];
  currentSkillLabel?: string;
  recommendedSkill?: CanonicalUnknownObject;
  extensions?: CanonicalUnknownObject;
}>;

export type AlignmentCanonicalPayload = CanonicalPayloadBase & {
  payloadVersion?: number;
  alignment: CanonicalAlignmentEnvelope;
  breakdown: CanonicalBreakdownEnvelope;
  alignmentCheckpoint?: CanonicalAlignmentCheckpoint;
  coordinatorHitl?: CanonicalUnknownObject;
  contextPolicy?: CanonicalUnknownObject;
};

export type CanonicalAssetVariant = {
  variantDraftId: string;
  kind: string;
  tagLabel: string;
  description?: string;
  isDefault: boolean;
  visualDescription?: string;
  clothing?: string;
  hair?: string;
  makeup?: string;
  accessories: readonly string[];
  voiceName?: string;
  voiceId?: string;
  applicableScenes: readonly string[];
  timeOfDay?: string | null;
  weather?: string | null;
  season?: string | null;
  atmosphere?: string;
  usage?: string;
  props: readonly string[];
  metaData: CanonicalUnknownObject;
  extensions?: CanonicalUnknownObject;
};

export type CanonicalAssetItem = {
  schemaVersion: number;
  draftItemId: string;
  kind: string;
  name: string;
  description?: string;
  roleType?: string;
  isOffscreen?: boolean;
  gender?: string;
  race?: string;
  visualAge?: string;
  location?: string | null;
  realWorldLocation?: string;
  propType?: string | null;
  ownerCharacter?: string;
  ownerLocation?: string;
  usage?: string;
  defaultVariantDraftId?: string;
  variants: readonly CanonicalAssetVariant[];
  metaData: CanonicalUnknownObject;
  extensions?: CanonicalUnknownObject;
};

export type AssetDraftResult = {
  schemaVersion?: number;
  characters: readonly CanonicalAssetItem[];
  locations: readonly CanonicalAssetItem[];
  props: readonly CanonicalAssetItem[];
};

export type CanonicalAssetConfirmations = Partial<
  Record<'characters' | 'locations' | 'props', Readonly<{ names: boolean; labels: boolean }>>
>;

export type CanonicalPersistedAssetIds = Readonly<
  Record<'characters' | 'locations' | 'props', readonly string[]>
>;

export type AssetCanonicalPayload = CanonicalPayloadBase & {
  assetConfirmations?: CanonicalAssetConfirmations;
  assetResult?: AssetDraftResult;
  assetChecklistDraft?: AssetDraftResult;
  persistedAssetIds?: CanonicalPersistedAssetIds;
  assetVersion?: number;
};

export type CanonicalStoryboardAssetReference =
  | string
  | Readonly<{
      name: string;
      draftItemId?: string;
      variantDraftId?: string;
      tagLabel?: string;
      extensions?: CanonicalUnknownObject;
    }>;

export type CanonicalStoryboardDialogue = Readonly<{
  content: string;
  dialogueType?: number;
  characterName?: string;
  emotion?: string;
  sequenceOrder?: number;
  extensions?: CanonicalUnknownObject;
}>;

export type CanonicalStoryboardItem = Readonly<{
  draftItemId?: string;
  title: string;
  shotNumber?: number;
  sceneId?: string;
  sceneNumber?: string;
  sceneTitle?: string;
  sceneOrder?: number;
  description: string;
  shootingDescription: string;
  shotSize: string;
  cameraMovement: string;
  estimatedDuration: number;
  dialogues: readonly CanonicalStoryboardDialogue[];
  characters: readonly CanonicalStoryboardAssetReference[];
  locations: readonly CanonicalStoryboardAssetReference[];
  props: readonly CanonicalStoryboardAssetReference[];
  metaData: CanonicalUnknownObject;
  extensions?: CanonicalUnknownObject;
}>;

export type StoryboardDraftResult = {
  storyboards: readonly CanonicalStoryboardItem[];
  extensions?: CanonicalUnknownObject;
};

export type StoryboardDraftIntegrity = {
  hasUnassociatedScene: boolean;
  unassociatedCount: number;
  storyboardCount: number;
};

export type StoryboardReviewRoute = 'fast_track' | 'selective_c1' | 'full_c1';
export type StoryboardC1Status =
  | 'not_required'
  | 'pending'
  | 'waiting_user'
  | 'confirmed'
  | 'skipped';

export type StoryboardRiskSignal = Readonly<{
  code: string;
  score: number;
  sceneId?: string;
  detail?: string;
}>;

export type StoryboardReviewProjection = Readonly<{
  route: StoryboardReviewRoute;
  projectScore: number;
  sceneScores: Readonly<Record<string, number>>;
  reviewSceneIds: readonly string[];
  signals: readonly StoryboardRiskSignal[];
  policyVersion: number;
  assessmentVersion: number;
  c1Status: StoryboardC1Status;
  c1Version?: number;
  c1SceneIds?: readonly string[];
  riskAcknowledged?: boolean;
  c1ResolvedAt?: string;
}>;

export type StoryboardStructureShot = Readonly<{
  order: number;
  purpose: string;
  shotSize: string;
  cameraMovement: string;
  assetAnchors: readonly string[];
}>;

export type StoryboardStructureScene = Readonly<{
  sceneId: string;
  sceneNumber: string;
  sceneOrder: number;
  objective: string;
  targetDuration: number;
  shotCount: number;
  shots: readonly StoryboardStructureShot[];
}>;

export type StoryboardStructureProjection = Readonly<{
  route: StoryboardReviewRoute;
  assessmentVersion: number;
  version: number;
  status: StoryboardC1Status;
  scenes: readonly StoryboardStructureScene[];
  riskAcknowledged?: boolean;
  resolvedAt?: string;
}>;

/** 导演补充里的全片时长区间；草稿实际总时长由前端按当前镜头实时计算。 */
export type StoryboardDurationCheck = Readonly<{
  minimumSeconds: number;
  maximumSeconds: number;
  toleranceSeconds?: number;
  dialogueSeconds?: number;
}>;

export type StoryboardQuantityConstraint = Readonly<{
  target: number;
  minimum: number | null;
  maximum: number | null;
  mode: 'exact' | 'range' | 'approximate';
}>;

export type StoryboardCanonicalPayload = CanonicalPayloadBase & {
  storyboardResult?: StoryboardDraftResult;
  storyboardDurationCheck?: StoryboardDurationCheck;
  storyboardQuantityConstraint?: StoryboardQuantityConstraint;
  storyboardDraftIntegrity?: StoryboardDraftIntegrity;
  storyboardVersion?: number;
  canRestorePreviousStoryboard?: boolean;
  persistedStoryboardIds?: readonly string[];
  storyboardReview?: StoryboardReviewProjection;
  storyboardStructure?: StoryboardStructureProjection;
  recoverable?: boolean;
  missingSceneOrders?: readonly number[];
};

type CanonicalSession<TSessionType extends number, TPayload> = Omit<
  AISession,
  | 'sessionType'
  | 'sourceType'
  | 'payload'
  | 'projectId'
  | 'stageStatus'
  | 'stageVersion'
  | 'sourceSessionId'
  | 'deletedAt'
> & {
  sessionType: TSessionType;
  sourceType: 1;
  projectId: string | null;
  payload: TPayload;
  stageStatus: CanonicalStageStatus | null;
  stageVersion: number | null;
  sourceSessionId: string | null;
  deletedAt: string | null;
};

export type AlignmentCanonicalSession = CanonicalSession<1, AlignmentCanonicalPayload>;
export type AssetCanonicalSession = CanonicalSession<3, AssetCanonicalPayload>;
export type StoryboardCanonicalSession = CanonicalSession<4, StoryboardCanonicalPayload>;

export type CanonicalAISession =
  | AlignmentCanonicalSession
  | AssetCanonicalSession
  | StoryboardCanonicalSession;
